import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  fetchContract,
  fetchContractDownload,
  fetchLeadContracts,
  generateContractFromItinerary,
  resendContract,
  fetchSigningLink,
  voidContract,
} from './contracts.api';

import { extractServerError } from '@/lib/api-error';

const extractMessage = (err: unknown): string | null => extractServerError(err).message;

export const contractsQK = {
  leadList: (leadId: string) => ['contracts', 'lead', leadId] as const,
  detail: (id: string) => ['contracts', 'detail', id] as const,
};

/** Statuses still moving through the signing flow - webhook-driven, so the
 *  timeline should refresh itself while any contract sits in one of these. */
const LIVE_STATUSES = new Set(['DRAFT', 'SENT', 'VIEWED', 'CUSTOMER_SIGNED']);

export const useLeadContracts = (leadId: string, enabled = true) =>
  useQuery({
    queryKey: contractsQK.leadList(leadId),
    queryFn: () => fetchLeadContracts(leadId),
    enabled,
    staleTime: 15_000,
    // Live timeline: while a contract is mid-signing, poll every 5s so
    // Sent -> Viewed -> Signed -> Completed appears without a manual
    // reload. Once everything is terminal the polling stops.
    refetchInterval: (query) => {
      const contracts = query.state.data;
      const hasLive =
        Array.isArray(contracts) && contracts.some((c) => LIVE_STATUSES.has(c.status));
      return hasLive ? 5_000 : false;
    },
    refetchOnWindowFocus: true,
  });

export const useContractDetail = (id: string | null) =>
  useQuery({
    queryKey: contractsQK.detail(id ?? ''),
    queryFn: () => fetchContract(id as string),
    enabled: Boolean(id),
    staleTime: 15_000,
  });

export const useGenerateContract = (leadId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itineraryId: string) => generateContractFromItinerary(itineraryId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contractsQK.leadList(leadId) });
      toast.success('Contract generated and sent for signature');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to generate the contract'),
  });
};

export const useResendContract = (leadId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => resendContract(id),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: contractsQK.leadList(leadId) });
      const to = `${res.resentTo.join(', ')}${res.whatsapp ? ' and on WhatsApp' : ''}`;
      toast.success(
        res.linkState === 'regenerated'
          ? `The old signing link was gone - a fresh contract was sent to ${to}`
          : res.linkState === 'renewed'
            ? `Signing link had expired - renewed and sent to ${to}`
            : `Signing invitation re-sent to ${to}`,
      );
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to re-send the contract'),
  });
};

/** "Copy link": fetch the live signing link and put it on the clipboard. */
export const useCopySigningLink = (leadId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fetchSigningLink(id),
    onSuccess: async (res) => {
      if (res.linkState === 'regenerated' || res.linkState === 'renewed') {
        void qc.invalidateQueries({ queryKey: contractsQK.leadList(leadId) });
      }
      try {
        await navigator.clipboard.writeText(res.url);
        toast.success(
          res.linkState === 'regenerated'
            ? 'Old link was gone - a fresh contract was sent; its link is copied'
            : res.linkState === 'renewed'
              ? 'Link had expired - renewed and copied'
              : 'Signing link copied',
        );
      } catch {
        // Clipboard blocked (no focus / permission): let them copy by hand.
        window.prompt('Copy the signing link', res.url);
      }
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Could not get the signing link'),
  });
};

export const useVoidContract = (leadId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => voidContract(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contractsQK.leadList(leadId) });
      toast.success('Contract voided');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to void the contract'),
  });
};

export const useDownloadContract = () =>
  useMutation({
    mutationFn: (id: string) => fetchContractDownload(id),
    onSuccess: (res) => {
      window.open(res.url, '_blank', 'noopener');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to fetch the contract PDF'),
  });
